import Layout from "../components/Layout";
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  MapPin,
  Shield,
  Star,
  Target,
  Users,
  Zap,
} from "lucide-react";
import { motion } from "motion/react";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

const fadeLeft = (delay = 0) => ({
  initial: { opacity: 0, x: -30 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true },
  transition: { duration: 0.55, delay },
});

const fadeRight = (delay = 0) => ({
  initial: { opacity: 0, x: 30 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true },
  transition: { duration: 0.55, delay },
});

interface Program {
  title: string;
  subtitle?: string;
  href: string;
  icon: React.ElementType;
  description: string;
  benefits?: string[];
  tags: string[];
  accentLabel: string;
}

const programs: Program[] = [
  {
    title: "Jiu Jitsu",
    href: "/programs/jiu-jitsu",
    icon: Target,
    accentLabel: "Adult Program",
    description:
      "Train with a focus on adaptability, control, and real-world application. Each class builds your ability to think, react, and perform under pressure.",
    tags: ["Adults", "All Levels", "No-Gi", "Fundamentals"],
  },
  {
    title: "MMA",
    href: "/programs/mma",
    icon: Zap,
    accentLabel: "All Ages",
    description:
      "A complete system combining grappling, striking, and wrestling. Ideal for those who want well-rounded skills or competitive training.",
    tags: ["Striking", "Grappling", "Wrestling", "Competition"],
  },
  {
    title: "Muay Thai & Striking",
    href: "/programs/muay-thai",
    icon: Star,
    accentLabel: "All Levels",
    description:
      "Develop speed, precision, and power while improving conditioning and confidence.",
    tags: ["Striking", "Kickboxing", "Conditioning", "Beginners Welcome"],
  },
  {
    title: "Wrestling",
    href: "/programs/wrestling",
    icon: Shield,
    accentLabel: "All Ages",
    description:
      "Build explosive movement, takedowns, and positional control that enhance both Jiu Jitsu and MMA.",
    tags: ["Takedowns", "Mat Control", "Explosiveness", "Cross-Training"],
  },
  {
    title: "Kids Jiu Jitsu",
    subtitle: "Ages 4–10",
    href: "/programs/kids-jiu-jitsu",
    icon: Users,
    accentLabel: "Ages 4–10",
    description:
      "Structured classes create a positive environment where kids learn while staying engaged.",
    benefits: [
      "Confidence",
      "Discipline",
      "Focus",
      "Respect",
      "Anti-bullying skills",
    ],
    tags: ["Kids", "Ages 4–10", "Fun", "Safe Environment"],
  },
  {
    title: "Teens Jiu Jitsu",
    subtitle: "Ages 11–16",
    href: "/programs/teens-jiu-jitsu",
    icon: ArrowRight,
    accentLabel: "Ages 11–16",
    description:
      "Teens gain real-world skills while training in a supportive, structured environment.",
    benefits: [
      "Leadership",
      "Mental toughness",
      "Confidence under pressure",
      "Physical fitness",
    ],
    tags: ["Teens", "Ages 11–16", "Structured", "Growth Mindset"],
  },
];

const serviceAreas = ["Eugene", "Thurston", "Glenwood", "Fairmont"];

export default function ProgramsPage() {
  return (
    <Layout>
      <title>
        Martial Arts Programs | Superhero Jiu Jitsu Academy in Springfield, OR
      </title>
      <meta
        name="description"
        content="Explore the martial arts programs offered at Superhero Jiu Jitsu Academy in Springfield, OR. Build skill, confidence, and fitness at every level."
      />

      {/* ── HERO SECTION ── */}
      <section
        className="relative bg-hero py-24 px-4 text-center overflow-hidden"
        data-ocid="programs-hero"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('/assets/images/programs hero image.jpg')",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-hero" aria-hidden="true" />
        <div className="absolute inset-0 bg-hero/60" aria-hidden="true" />
        <div className="relative container mx-auto max-w-3xl">
          <motion.div {...fadeUp(0.05)}>
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-4 border border-gold/40 px-3 py-1 rounded-full">
              Springfield, OR
            </span>
          </motion.div>
          <motion.h1
            {...fadeUp(0.1)}
            className="font-display font-black text-4xl sm:text-5xl md:text-6xl text-hero-foreground leading-tight text-balance mb-5"
          >
            Martial Arts Training Programs{" "}
            <span className="text-gold">in Springfield, OR</span>
          </motion.h1>
          <motion.p
            {...fadeUp(0.15)}
            className="font-body text-hero-foreground text-lg leading-relaxed max-w-2xl mx-auto opacity-90"
          >
            Explore programs designed to help you build skill, confidence, and
            fitness at every level.
          </motion.p>
        </div>
      </section>

      {/* Gold divider */}
      <div className="h-1 bg-gradient-accent" />

      {/* ── PROGRAMS GRID ── */}
      <section className="bg-background py-16 px-4" data-ocid="programs-list">
        <div className="container mx-auto max-w-6xl">
          <motion.div {...fadeUp(0.05)} className="text-center mb-12">
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              For All Ages & Levels
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground text-balance">
              Find Your Program
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {programs.map(
              (
                {
                  title,
                  subtitle,
                  href,
                  icon: Icon,
                  description,
                  benefits,
                  tags,
                  accentLabel,
                },
                i,
              ) => (
                <motion.div
                  key={href}
                  {...(i % 2 === 0
                    ? fadeLeft(0.05 + i * 0.07)
                    : fadeRight(0.05 + i * 0.07))}
                >
                  <Link
                    to={href}
                    className="group flex flex-col bg-card border border-border rounded-2xl shadow-card hover:shadow-elevated transition-smooth hover:-translate-y-0.5 overflow-hidden h-full cursor-pointer"
                    data-ocid={`program-card-${href.replace("/programs/", "")}`}
                  >
                    {/* Card header accent */}
                    <div className="h-1.5 bg-gradient-accent" />

                    <div className="p-6 flex flex-col gap-4 flex-1">
                      {/* Icon + label row */}
                      <div className="flex items-start justify-between gap-3">
                        <span className="w-12 h-12 rounded-xl bg-gradient-accent flex items-center justify-center shadow-gold flex-shrink-0">
                          <Icon className="w-5 h-5 text-foreground" />
                        </span>
                        <span className="inline-block text-xs font-display font-semibold tracking-wider uppercase text-gold border border-gold/40 px-2.5 py-1 rounded-full mt-1">
                          {accentLabel}
                        </span>
                      </div>

                      {/* Title */}
                      <div>
                        <h3 className="font-display font-black text-xl text-foreground leading-tight">
                          {title}
                        </h3>
                        {subtitle && (
                          <p className="font-body text-sm text-muted-foreground mt-0.5">
                            {subtitle}
                          </p>
                        )}
                      </div>

                      {/* Description */}
                      <p className="font-body text-sm text-muted-foreground leading-relaxed flex-1">
                        {description}
                      </p>

                      {/* Benefits list (kids / teens) */}
                      {benefits && (
                        <ul className="space-y-1">
                          {benefits.map((b) => (
                            <li
                              key={b}
                              className="flex items-center gap-2 text-sm font-body text-foreground"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                              {b}
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Tags */}
                      <div className="flex flex-wrap gap-1.5 mt-auto pt-2">
                        {tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-xs font-body px-2.5 py-1 rounded-full bg-muted border border-border text-muted-foreground"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      {/* CTA row */}
                      <div className="pt-2 border-t border-border flex items-center justify-between">
                        <span className="text-sm font-display font-semibold text-foreground group-hover:text-gold transition-colors-fast">
                          View Program
                        </span>
                        <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center group-hover:bg-gradient-accent group-hover:shadow-gold transition-smooth flex-shrink-0">
                          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors-fast" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ),
            )}
          </div>
        </div>
      </section>

      {/* ── CTA SECTION ── */}
      <section
        className="py-20 px-4 text-center relative"
        style={{
          background:
            "linear-gradient(135deg, oklch(0.12 0.06 255) 0%, oklch(0.18 0.08 270) 50%, oklch(0.14 0.05 255) 100%)",
        }}
        data-ocid="programs-cta"
      >
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 50% 50%, oklch(0.68 0.16 82) 0%, transparent 60%)",
          }}
          aria-hidden="true"
        />
        <motion.div
          {...fadeUp(0.1)}
          className="relative container mx-auto max-w-2xl"
        >
          <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3 border border-gold/40 px-3 py-1 rounded-full">
            First Class Is Free
          </span>
          <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-hero-foreground leading-tight mb-4 text-balance">
            Not Sure Where to Start?
          </h2>
          <p className="text-hero-foreground font-body text-lg leading-relaxed mb-3 max-w-lg mx-auto">
            Try any program free. Our coaches will help you find the right fit
            based on your goals, age, and experience.
          </p>
          <p className="text-hero-foreground font-body text-sm mb-8 opacity-70">
            Must book in advance. No experience needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact-us"
              className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-display font-bold text-base bg-gradient-accent text-foreground shadow-gold hover:opacity-90 active:scale-[0.98] transition-smooth cursor-pointer"
              data-ocid="programs-cta-button"
            >
              Book a Free Class
            </Link>
            <a
              href="tel:+15416003371"
              className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-display font-bold text-base border-2 border-primary-foreground/60 text-hero-foreground hover:bg-primary-foreground/10 transition-smooth cursor-pointer"
              data-ocid="programs-cta-phone"
            >
              Call (541) 600-3371
            </a>
          </div>
        </motion.div>
      </section>

      {/* ── LOCAL SEO SECTION ── */}
      <section
        className="bg-background py-10 px-4 border-t border-border"
        data-ocid="programs-local-seo"
      >
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div {...fadeUp(0.1)}>
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-gold flex-shrink-0" />
              <span className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                Serving Springfield &amp; Surrounding Areas
              </span>
            </div>
            <h2 className="font-display font-bold text-lg text-foreground mb-3">
              Martial Arts Programs in Springfield, OR
            </h2>
            <p className="font-body text-muted-foreground text-sm leading-relaxed max-w-2xl mx-auto mb-4">
              Serving Springfield, OR — also serving Eugene, Thurston, Glenwood,
              Fairmont, and surrounding areas.
            </p>
            <address className="not-italic font-body text-sm text-muted-foreground mb-5">
              <span className="font-medium text-foreground">
                1485 Mohawk Blvd, Springfield, OR 97477
              </span>
              {" · "}
              <a
                href="tel:+15416003371"
                className="text-foreground font-medium hover:text-gold transition-colors-fast"
              >
                (541) 600-3371
              </a>
              {" · "}
              <a
                href="mailto:shjjoffice@gmail.com"
                className="text-foreground font-medium hover:text-gold transition-colors-fast"
              >
                shjjoffice@gmail.com
              </a>
            </address>
            <div className="flex flex-wrap justify-center gap-2">
              {["Springfield", ...serviceAreas, "Lane County"].map((area) => (
                <span
                  key={area}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-muted border border-border text-sm font-body text-muted-foreground"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                  {area}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
